import type { TFunction } from "i18next";

import type { NotificationItem } from "@/api/notifications";
import { displaySessionTitle } from "@/features/sessions/sessionTitle";

function fact(item: NotificationItem, key: string): string | null {
  const value = item.facts[key];
  return typeof value === "string" && value.trim() ? value : null;
}

/** The notification's one line, in the viewer's language.
 *
 *  Rendered from the type's key (`notifications.types.<stem>.title`) and
 *  the facts the server resolved when the list was read. A name or title
 *  the viewer may no longer see arrives as `null` and reads as a neutral
 *  word; a type this build doesn't know reads as a generic line. */
export function notificationTitle(t: TFunction, item: NotificationItem): string {
  if (!item.stem) return t("notifications.generic");
  if (item.stem === "request_updated" && item.facts["status"] === "merged") {
    // An FW item you follow was merged into another; `into` is the
    // target's number, sent only when the target is public.
    const into = item.facts["into"];
    return typeof into === "number"
      ? t("notifications.types.request_updated.merged_into", { fw: into })
      : t("notifications.types.request_updated.merged");
  }
  if (item.stem === "billing_attention") {
    // A refund, dispute or ended plan that needs a platform admin; the
    // reason picks the sentence.
    const reason = fact(item, "reason");
    return t(`notifications.types.billing_attention.reasons.${reason ?? "other"}`, {
      org: item.org?.name ?? t("notifications.your_org"),
      defaultValue: t("notifications.types.billing_attention.title", {
        org: item.org?.name ?? t("notifications.your_org"),
      }),
    });
  }
  return t(`notifications.types.${item.stem}.title`, {
    actor: fact(item, "actor_name") ?? t("notifications.someone"),
    session: displaySessionTitle(t, fact(item, "session_title")) || t("notifications.a_session"),
    document: fact(item, "document_title") ?? t("notifications.a_document"),
    org: item.org?.name ?? t("notifications.your_org"),
    // The sign-in rules a notice is about (``auth_match.reverify``).
    rules: fact(item, "rules") ?? "",
    defaultValue: t("notifications.generic"),
  });
}
